import { Form, Formik } from 'formik'
import React, { useState } from 'react'
import * as Yup from 'yup'

import type { DragEndEvent } from '@dnd-kit/core'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button, Fieldset, Icon, Input } from '@fourthwall/components'
import { DragIcon, PlusIcon, TrashIcon } from '@fourthwall/icons'
import { isProduction } from '@fourthwall/utils'

import type { App } from '../../types'
import { CredentialDisplay } from '../BasicInformationTab/components/CredentialDisplay/CredentialDisplay'
import { useGetPlatformAppClientSecret } from '../BasicInformationTab/hooks/useGetPlatformAppClientSecret'
import * as S from './OAuthTab.styled'
import { useUpdatePlatformAppClientRedirectUris } from './hooks/useUpdatePlatformAppClientRedirectUris'

type RedirectUrisFormValues = {
  redirectUris: string[]
}

type OAuthTabProps = {
  app: App
}

type SortableUriItemProps = {
  uri: string
  isDefault: boolean
  onRemove: () => void
}

const validationSchema = Yup.object().shape({
  redirectUris: Yup.array().of(Yup.string()),
})

const getInitialValues = (app: App): RedirectUrisFormValues => ({
  redirectUris: app?.redirectUris || [],
})

const SortableUriItem = ({ uri, isDefault, onRemove }: SortableUriItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: uri,
  })

  return (
    <S.UriItem
      ref={setNodeRef}
      {...attributes}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <S.UriContent>
        <S.DragHandle {...listeners}>
          <Icon component={DragIcon} height={12} />
        </S.DragHandle>
        <S.UriText>{uri}</S.UriText>
        {isDefault && <S.DefaultBadge>Default</S.DefaultBadge>}
      </S.UriContent>
      <Button
        appearance="secondary"
        size="small"
        icon={<Icon component={TrashIcon} height={16} />}
        onClick={onRemove}
      />
    </S.UriItem>
  )
}

export const OAuthTab = ({ app }: OAuthTabProps) => {
  const { updatePlatformAppClientRedirectUris, isLoading } =
    useUpdatePlatformAppClientRedirectUris()
  const { getPlatformAppClientSecret } = useGetPlatformAppClientSecret()

  const handleRedirectUrisSave = (redirectUris: string[]) => {
    if (!app.id) return
    updatePlatformAppClientRedirectUris([{ clientId: app.id }, { redirectUris }])
  }

  const [newUri, setNewUri] = useState('')

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleSubmit = (values: RedirectUrisFormValues) => {
    handleRedirectUrisSave(values.redirectUris)
  }

  const handleAddUri = (
    setFieldValue: (field: string, value: any) => void,
    values: RedirectUrisFormValues,
  ) => {
    if (newUri.trim() && !values.redirectUris.includes(newUri.trim())) {
      const updatedUris = [...values.redirectUris, newUri.trim()]
      setFieldValue('redirectUris', updatedUris)
      setNewUri('')
    }
  }

  const handleRemoveUri = (
    setFieldValue: (field: string, value: any) => void,
    values: RedirectUrisFormValues,
    index: number,
  ) => {
    const updatedUris = values.redirectUris.filter((_, i) => i !== index)
    setFieldValue('redirectUris', updatedUris)
  }

  const handleDragEnd = (
    event: DragEndEvent,
    setFieldValue: (field: string, value: any) => void,
    values: RedirectUrisFormValues,
  ) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      const oldIndex = values.redirectUris.indexOf(active.id as string)
      const newIndex = values.redirectUris.indexOf(over?.id as string)

      if (oldIndex !== -1 && newIndex !== -1) {
        const updatedUris = [...values.redirectUris]
        const [movedUri] = updatedUris.splice(oldIndex, 1)
        updatedUris.splice(newIndex, 0, movedUri)
        setFieldValue('redirectUris', updatedUris)
      }
    }
  }

  return (
    <Formik<RedirectUrisFormValues>
      initialValues={getInitialValues(app)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ submitForm, setFieldValue, values }) => (
        <Form>
          <S.Container>
            {/* Credentials Section */}
            <S.CredentialsSection>
              <Fieldset>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <S.SectionLabel>Credentials</S.SectionLabel>
                  </Fieldset.Item>
                </Fieldset.Row>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <CredentialDisplay label="Client ID" value={app.id} copyable />
                  </Fieldset.Item>
                </Fieldset.Row>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <CredentialDisplay
                      label="Client Secret"
                      copyable
                      onReveal={async () => {
                        if (app.id) {
                          const data = await getPlatformAppClientSecret([{ clientId: app.id }])

                          return data?.secret || ''
                        }

                        return ''
                      }}
                    />
                  </Fieldset.Item>
                </Fieldset.Row>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <CredentialDisplay
                      label="Authorization URL"
                      value={`https://my-shop.${isProduction() ? '' : 'staging.'}fourthwall.com/admin/platform-apps/${app.id}/connect?redirect_uri=${encodeURIComponent(app.redirectUris?.[0] || '')}`}
                      copyable
                    />
                  </Fieldset.Item>
                </Fieldset.Row>
              </Fieldset>
            </S.CredentialsSection>

            {/* Redirect URIs Section */}
            <S.SectionLabel>Redirect URIs</S.SectionLabel>

            <S.Description>
              Configure the redirect URIs that your app will use for OAuth authentication. The first
              URI in the list will be used as the default.
            </S.Description>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => handleDragEnd(event, setFieldValue, values)}
            >
              <SortableContext items={values.redirectUris} strategy={verticalListSortingStrategy}>
                <S.UrisContainer>
                  {values.redirectUris.map((uri, index) => (
                    <SortableUriItem
                      key={uri}
                      uri={uri}
                      isDefault={index === 0}
                      onRemove={() => handleRemoveUri(setFieldValue, values, index)}
                    />
                  ))}
                </S.UrisContainer>
              </SortableContext>
            </DndContext>

            <S.AddUriContainer>
              <Input
                value={newUri}
                onChange={(e) => setNewUri(e.target.value)}
                placeholder="Enter redirect URI (e.g., https://your-app.com/callback)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddUri(setFieldValue, values)
                  }
                }}
              />
              <Button
                label="Add"
                icon={<PlusIcon />}
                onClick={() => handleAddUri(setFieldValue, values)}
                disabled={!newUri.trim()}
              />
            </S.AddUriContainer>

            <S.ButtonContainer>
              <Button
                label="Save Changes"
                appearance="primary"
                onClick={submitForm}
                disabled={isLoading}
                loading={isLoading}
              />
            </S.ButtonContainer>
          </S.Container>
        </Form>
      )}
    </Formik>
  )
}
